'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Image } from '@udemy-v2/react-core-components';
import { useI18n } from '@udemy-v2/i18n';
import { useMatchMedia } from '@udemy-v2/design-system-utils';
import classNames from 'classnames';
import { InstructorCommunityBannerInfoByInstructorIdQuery } from '@/generated/graphql';
import styles from './instructor-community-banner.module.scss';
export type InstructorCommunityBannerInfo = NonNullable<InstructorCommunityBannerInfoByInstructorIdQuery['instructorCommunityBannerInfoByInstructorId']>;
const BANNER_TEXT_COLOR = {
  DARK: 'var(--applied-text-default)',
  LIGHT: '#E9EAF2'
} as const;
export interface InstructorCommunityBannerProps {
  instructorName: string;
  instructorUrl?: string;
  bannerInfo: InstructorCommunityBannerInfo;
}
export function InstructorCommunityBanner(t0) {
  const $ = _c(48);
  const {
    instructorName,
    instructorUrl,
    bannerInfo
  } = t0;
  const {
    gettext,
    interpolate
  } = useI18n();
  const isMobile = useMatchMedia("sm-max");
  const bannerImageUrl = isMobile ? bannerInfo.mobileCommunityBannerImage : bannerInfo.communityBannerImage;
  if (!bannerImageUrl) {
    return null;
  }
  const textColor = isMobile ? BANNER_TEXT_COLOR.LIGHT : BANNER_TEXT_COLOR[bannerInfo.communityBannerTextColorPreference ?? "DARK"];
  const isLightText = textColor === BANNER_TEXT_COLOR.LIGHT;
  let t1;
  if ($[0] !== gettext || $[1] !== instructorName || $[2] !== interpolate) {
    t1 = interpolate(gettext("Join %(instructorName)s's Learning Community"), {
      instructorName
    }, true);
    $[0] = gettext;
    $[1] = instructorName;
    $[2] = interpolate;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const bannerTitle = t1;
  let t2;
  if ($[4] !== bannerInfo.isSubscribed || $[5] !== gettext) {
    t2 = bannerInfo.isSubscribed ? gettext("Go to community") : gettext("Join my community");
    $[4] = bannerInfo.isSubscribed;
    $[5] = gettext;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const primaryButtonText = t2;
  const primaryButtonHref = bannerInfo.isSubscribed ? bannerInfo.communityUrl : bannerInfo.communityPaywallUrl;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = classNames("ud-heading-lg", styles["banner-title"]);
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== bannerTitle) {
    t4 = <h3 className={t3}>{bannerTitle}</h3>;
    $[8] = bannerTitle;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== bannerInfo.description) {
    t5 = bannerInfo.description && <p className={classNames("ud-text-sm", styles["banner-description"])}>{bannerInfo.description}</p>;
    $[10] = bannerInfo.description;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== primaryButtonHref || $[13] !== primaryButtonText) {
    t6 = <Button udStyle="primary" size="small" componentClass="a" href={primaryButtonHref}>{primaryButtonText}</Button>;
    $[12] = primaryButtonHref;
    $[13] = primaryButtonText;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = isLightText ? "knockout-outline" : "secondary";
  let t8;
  if ($[15] !== gettext) {
    t8 = gettext("Learn more");
    $[15] = gettext;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== instructorUrl || $[18] !== t7 || $[19] !== t8) {
    t9 = <Button udStyle={t7} size="small" componentClass="a" href={instructorUrl}>{t8}</Button>;
    $[17] = instructorUrl;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t6 || $[22] !== t9) {
    t10 = <div className={styles["banner-buttons"]}>{t6}{t9}</div>;
    $[21] = t6;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t4 || $[26] !== t5) {
    t11 = <>{t4}{t5}{t10}</>;
    $[24] = t10;
    $[25] = t4;
    $[26] = t5;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const bannerContent = t11;
  if (isMobile) {
    let t12;
    if ($[28] !== bannerImageUrl) {
      t12 = <div className={styles["banner-photo-mobile"]}><Image src={bannerImageUrl} alt="" /></div>;
      $[28] = bannerImageUrl;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    let t13;
    if ($[30] !== textColor) {
      t13 = {
        color: textColor
      };
      $[30] = textColor;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    let t14;
    if ($[32] !== bannerContent || $[33] !== t13) {
      t14 = <div className={styles["banner-content-mobile"]} style={t13}>{bannerContent}</div>;
      $[32] = bannerContent;
      $[33] = t13;
      $[34] = t14;
    } else {
      t14 = $[34];
    }
    let t15;
    if ($[35] !== t12 || $[36] !== t14) {
      t15 = <div className={styles["banner-container-mobile"]} data-purpose="instructor-community-banner-mobile">{t12}{t14}</div>;
      $[35] = t12;
      $[36] = t14;
      $[37] = t15;
    } else {
      t15 = $[37];
    }
    return t15;
  }
  const t12 = `url(${bannerImageUrl})`;
  let t13;
  if ($[38] !== t12) {
    t13 = {
      backgroundImage: t12
    };
    $[38] = t12;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== textColor) {
    t14 = {
      color: textColor
    };
    $[40] = textColor;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== bannerContent || $[43] !== t14) {
    t15 = <div className={styles["banner-content"]} style={t14}>{bannerContent}</div>;
    $[42] = bannerContent;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t13 || $[46] !== t15) {
    t16 = <div className={styles["banner-container"]} style={t13} data-purpose="instructor-community-banner">{t15}</div>;
    $[45] = t13;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  return t16;
}